import type { CSSProperties, ReactNode } from "react";
import type { ApiError } from "../lib/api";
import LogoLoader from "./LogoLoader";
import InfoDot from "./InfoDot";

/**
 * Loading, empty, error, stale. Four states, and each one has to look like
 * somebody decided it.
 *
 * The one that matters most here is the one people skip. An empty table on a
 * dashboard is ambiguous: it can mean "nothing needs you" or "this failed to
 * load and said nothing". In a system somebody makes payment decisions from,
 * those must never look the same, so `Empty` takes a tone and the good case
 * says out loud what it checked.
 */

/**
 * The page/section loading state. Every page hands this the body of a table or
 * panel that is still fetching, so it is the "empty table" a reader would
 * otherwise stare at. It now shows the animated VGH mark (see `LogoLoader`)
 * rather than a stack of shimmer bars -- the mark reads as "VGH is loading
 * this" where blank bars read as a half-built page. `rows` still sizes the space
 * so the swap does not shift anything; `Skeleton` below stays the brick for
 * pages that deliberately build a shaped placeholder.
 *
 * `fill` (default on) centres the mark in the viewport for a whole-body load; a
 * sub-section band that sits above other content passes `fill={false}` to stay
 * compact. See `LogoLoader`.
 */
export function Loading({ rows = 5, fill = true }: { rows?: number; fill?: boolean }) {
  return <LogoLoader rows={rows} fill={fill} />;
}

/**
 * One shimmering placeholder block, shaped to the thing it stands in for.
 *
 * `Loading` draws a stack of generic bars; this is the single brick those are
 * made of, exposed so a page can lay out a skeleton that matches its real
 * structure -- a figure-sized block here, a row of stat cards there -- instead
 * of leaving the space blank until the fetch lands. A blank space that fills in
 * reads as a page half-built; a skeleton of the right shape reads as a page
 * loading.
 */
export function Skeleton({
  w = "100%",
  h = 12,
  style,
}: {
  w?: string | number;
  h?: number;
  style?: CSSProperties;
}) {
  return (
    <div
      className="skeleton-row"
      style={{
        width: typeof w === "number" ? `${w}px` : w,
        height: h,
        margin: 0,
        ...style,
      }}
    />
  );
}

export function Empty({
  headline,
  detail,
  tone = "neutral",
  children,
}: {
  headline: string;
  detail?: ReactNode;
  /** "good" is for the deliberate nothing-to-do case. It is not the default. */
  tone?: "neutral" | "good";
  children?: ReactNode;
}) {
  return (
    <div className={`state ${tone === "good" ? "empty-good" : ""}`}>
      {/* The explanation sits behind the info icon, never as a paragraph on
          screen (Jonathan, 2026-10-15: "all these words ... hidden behind an
          info icon"). The headline says what is true; the icon says why. */}
      <div className="headline">
        {headline}
        {detail ? <InfoDot label="Why">{detail}</InfoDot> : null}
      </div>
      {children}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry?: () => void;
}) {
  // 0 is this client's own marker for "the request never reached anything".
  const isNetwork = error.status === 0;
  return (
    <div className="state error" role="alert">
      <div className="headline">
        {isNetwork ? "Could not reach the server." : "This did not load."}
      </div>
      <div className="detail">
        {error.detail}
        {!isNetwork && error.status ? ` (${error.status})` : ""}
        
      </div>
      {onRetry ? (
        <div style={{ marginTop: 12 }}>
          <button onClick={onRetry}>Try again</button>
        </div>
      ) : null}
    </div>
  );
}
